import { resolveOrganizationBrandName } from '../../utils/branding';
import { useI18n } from '../../context/useI18n';
import { Calendar, Trophy, Video, TrendingUp, MessageCircle } from 'lucide-react';
import { buildWaMeUrl, buildSupportWaUrl } from '../../utils/whatsappSupport';
import { useAuth } from '../../context/useAuth';

const TILES = [
  { id: 'classes', icon: Calendar, nav: 'liveclasses', labelKey: 'd28d.hub.classes', defaultLabel: 'Clases en vivo', hintKey: 'd28d.hub.classes_hint', defaultHint: 'Calendario, inscripción y Zoom' },
  { id: 'recordings', icon: Video, nav: 'd28d-gallery', labelKey: 'd28d.hub.recordings', defaultLabel: 'Clases grabadas', hintKey: 'd28d.hub.recordings_hint', defaultHint: 'Revisa sesiones pasadas' },
  { id: 'progress', icon: TrendingUp, nav: 'mi-progreso', labelKey: 'd28d.hub.progress', defaultLabel: 'Mi progreso', hintKey: 'd28d.hub.progress_hint', defaultHint: 'Asistencia y avance del ciclo' },
  { id: 'premiacion', icon: Trophy, nav: 'mi-progreso', tab: 'medidas', labelKey: 'd28d.hub.premiacion', defaultLabel: 'Premiación', hintKey: 'd28d.hub.premiacion_hint', defaultHint: 'Fotos, medidas y postulación' },
];

function HubTile({ tile, onNavigate, t }) {
  const Icon = tile.icon;
  return (
    <button
      type="button"
      className="ef-stat-card text-left hover:border-[var(--d28d-yellow)] transition p-4"
      onClick={() => {
        if (tile.tab) onNavigate?.(tile.nav, tile.tab);
        else onNavigate?.(tile.nav);
      }}
    >
      <div className="flex items-start gap-3">
        <span className="p-2 rounded-xl bg-[var(--d28d-yellow)]/15 text-[var(--d28d-yellow)]">
          <Icon className="w-5 h-5" />
        </span>
        <div>
          <p className="font-bold text-[var(--d28d-text)]">{t(tile.labelKey, tile.defaultLabel)}</p>
          <p className="text-xs d28d-text-muted mt-1">{t(tile.hintKey, tile.defaultHint)}</p>
        </div>
      </div>
    </button>
  );
}

export default function D28dUserHub({
  onNavigate,
  gymBrand = null,
  hidePremiacion = false,
  sedeFinalMode = false,
}) {
  const { t } = useI18n();
  const { user } = useAuth();
  const gymLabel = resolveOrganizationBrandName({ entity: gymBrand });
  const visibleTiles = TILES.filter((tile) => {
    if (hidePremiacion && tile.id === 'premiacion') return false;
    return true;
  }).map((tile) => {
    if (sedeFinalMode && tile.id === 'progress') {
      return {
        ...tile,
        hintKey: 'd28d.hub.progress_sede_hint',
        defaultHint: 'Asistencia, inscripciones y clases grabadas',
      };
    }
    return tile;
  });

  const openWhatsApp = () => {
    const url = buildSupportWaUrl({
      gym: gymBrand,
      sedeFirst: sedeFinalMode || gymBrand?.parent_gym_id != null,
    });
    if (!url) {
      onNavigate?.('support');
      return;
    }
    const defaultMsg = hidePremiacion || sedeFinalMode
      ? (gymLabel
        ? t('d28d.hub.wa_support_gym', 'Hola, necesito soporte — {brand}', { brand: gymLabel })
        : t('d28d.hub.wa_support', 'Hola, necesito soporte'))
      : t('d28d.hub.wa_support_d28d', 'Hola, necesito soporte de Fit Live · Programa');
    const wa = gymBrand?.support_whatsapp;
    const finalUrl = wa
      ? buildWaMeUrl(wa, gymBrand?.welcome_message || defaultMsg)
      : url;
    window.open(finalUrl, '_blank', 'noopener,noreferrer');
  };

  const subtitle = sedeFinalMode
    ? t('d28d.hub.subtitle_sede', 'Clases en vivo, grabaciones y tu progreso en la sede')
    : hidePremiacion
      ? t('d28d.hub.subtitle_gym', 'Todo lo que necesitas para tu transformación')
      : t('d28d.hub.subtitle', 'Todo lo que necesitas para tu transformación de 28 días');

  const whatsappHint = hidePremiacion || sedeFinalMode
    ? t('d28d.hub.whatsapp_gym_hint', 'Escríbenos por WhatsApp cuando lo necesites')
    : t('d28d.hub.whatsapp_hint', 'Contacta al equipo de soporte al instante');

  return (
    <section className="space-y-3">
      <div>
        <h2 className="font-bold text-sm uppercase tracking-wide text-[var(--d28d-text)]">
          {gymLabel || t('d28d.hub.title', 'Tu gimnasio virtual')}
        </h2>
        <p className="text-xs d28d-text-muted mt-1">{subtitle}</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {hidePremiacion ? (
          <>
            {visibleTiles.map((tile) => (
              <HubTile key={tile.id} tile={tile} onNavigate={onNavigate} t={t} />
            ))}
            <button
              type="button"
              className="ef-stat-card text-left hover:border-emerald-400 transition p-4"
              onClick={openWhatsApp}
            >
              <div className="flex items-start gap-3">
                <span className="p-2 rounded-xl bg-emerald-500/15 text-emerald-600">
                  <MessageCircle className="w-5 h-5" />
                </span>
                <div>
                  <p className="font-bold text-[var(--d28d-text)]">{t('d28d.hub.whatsapp', 'Soporte WhatsApp')}</p>
                  <p className="text-xs d28d-text-muted mt-1">{whatsappHint}</p>
                </div>
              </div>
            </button>
          </>
        ) : (
          <>
            {visibleTiles.map((tile) => (
              <HubTile key={tile.id} tile={tile} onNavigate={onNavigate} t={t} />
            ))}
            <button
              type="button"
              className="ef-stat-card text-left hover:border-emerald-400 transition p-4 sm:col-span-2"
              onClick={openWhatsApp}
            >
              <div className="flex items-start gap-3">
                <span className="p-2 rounded-xl bg-emerald-500/15 text-emerald-600">
                  <MessageCircle className="w-5 h-5" />
                </span>
                <div>
                  <p className="font-bold text-[var(--d28d-text)]">{t('d28d.hub.whatsapp', 'Soporte WhatsApp')}</p>
                  <p className="text-xs d28d-text-muted mt-1">{whatsappHint}</p>
                </div>
              </div>
            </button>
          </>
        )}
      </div>
    </section>
  );
}
